import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase/config';
import { doc, setDoc } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaUser, FaPhone } from 'react-icons/fa';

export const EmployeeRegister = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      return setError(t('register.passwordsDontMatch'));
    }

    if (!firstName || !lastName || !phone) {
      return setError(t('employeeRegister.fillAllFields'));
    }

    try {
      setError('');
      setLoading(true);
      console.log('Registrando empleado con email:', email);
      const userCredential = await signUp(email, password);
      console.log('Empleado creado en Auth:', userCredential.user.uid);
      
      // Guardar datos adicionales en Firestore
      if (userCredential && userCredential.user) {
        const userData = {
          firstName,
          lastName,
          email,
          phone,
          role: 'employee',
          status: 'active',
          business: 'none', // Campo para asignar Business UID después
          createdAt: Timestamp.now(),
          onBoarding: false,
          language: i18n.language,
          // Campos específicos para empleados
          employeeProfile: {
            isComplete: false,
            completedSteps: [],
            profilePhoto: null,
            emergencyContact: null,
            skills: [],
            certifications: [],
            performance: {
              rating: 0,
              reviews: [],
              lastReview: null
            },
            attendance: {
              totalDays: 0,
              absences: 0,
              lateArrivals: 0
            }
          }
        };
        console.log('Guardando empleado en Firestore:', userData);
        await setDoc(doc(db, 'users', userCredential.user.uid), userData);
        console.log('Documento de empleado guardado en Firestore con UID:', userCredential.user.uid);
      }
      // Redirigir al dashboard de empleado después de registro
      navigate('/employee-dashboard');
    } catch (error) {
      console.error('Error en el registro de empleado:', error);
      setError(t('employeeRegister.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-full flex justify-center items-center animate-fadeIn">
      <div className="bg-white/95 p-8 rounded-2xl shadow-2xl w-full max-w-4xl relative z-10 backdrop-blur-md border border-white/20 transform transition-all duration-500 hover:scale-[1.02] hover:shadow-3xl">
        {/* Logo con animación */}
        <div className="flex justify-center mb-8">
          <div className="relative group">
            <img 
              src="https://firebasestorage.googleapis.com/v0/b/brix-v2.firebasestorage.app/o/Marca%2FRecurso%205.svg?alt=media&token=65d56a39-541f-4ec0-864f-fc2105cf9c54" 
              alt="Brix Logo" 
              className="w-32 max-h-20 object-contain drop-shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:drop-shadow-2xl"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
          </div>
        </div>

        {/* Título con efecto de gradiente */}
        <h1 className="text-3xl font-bold text-center mb-8 bg-gradient-to-r from-gray-800 via-red-600 to-gray-800 bg-clip-text text-transparent animate-pulse flex items-center justify-center gap-2">
          <FaUser className="text-red-500 animate-pulse" /> {t('employeeRegister.title', 'Registro de Empleado')}
        </h1>

        {/* Mensaje de error con animación */}
        {error && (
          <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 rounded-lg animate-shake">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información Personal */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nombre */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('register.firstName', 'Nombre')} *
              </label>
              <div className="relative">
                <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                 <input
                   type="text"
                   value={firstName}
                   onChange={(e) => setFirstName(e.target.value)}
                   className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300 bg-white/50 backdrop-blur-sm"
                   placeholder={t('register.firstNamePlaceholder', 'Ingresa tu nombre')}
                   required
                 />
              </div>
            </div>

            {/* Apellido */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('register.lastName', 'Apellido')} *
              </label>
              <div className="relative">
                <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                 <input
                   type="text"
                   value={lastName}
                   onChange={(e) => setLastName(e.target.value)}
                   className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300 bg-white/50 backdrop-blur-sm"
                   placeholder={t('register.lastNamePlaceholder', 'Ingresa tu apellido')}
                   required
                 />
              </div>
            </div>
          </div>

          {/* Información de Contacto */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Email */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('register.email', 'Email')} *
              </label>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                 <input
                   type="email"
                   value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300 bg-white/50 backdrop-blur-sm"
                   placeholder={t('register.emailPlaceholder', 'tu@email.com')}
                   required
                 />
              </div>
            </div>

            {/* Teléfono */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('employeeRegister.phone', 'Teléfono')} *
              </label>
              <div className="relative">
                <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                 <input
                   type="tel"
                   value={phone}
                   onChange={(e) => setPhone(e.target.value)}
                   className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300 bg-white/50 backdrop-blur-sm"
                   placeholder={t('employeeRegister.phonePlaceholder', '+1 (555) 123-4567')}
                   required
                 />
              </div>
            </div>
          </div>

          

          {/* Contraseñas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contraseña */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('register.password', 'Contraseña')} *
              </label>
              <div className="relative">
                <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                 <input
                   type={showPassword ? 'text' : 'password'}
                   value={password}
                   onChange={(e) => setPassword(e.target.value)}
                   className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300 bg-white/50 backdrop-blur-sm"
                   placeholder={t('register.passwordPlaceholder', 'Mínimo 6 caracteres')}
                   required
                 />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* Confirmar Contraseña */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('register.confirmPassword', 'Confirmar Contraseña')} *
              </label>
              <div className="relative">
                <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                 <input
                   type={showConfirmPassword ? 'text' : 'password'}
                   value={confirmPassword}
                   onChange={(e) => setConfirmPassword(e.target.value)}
                   className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300 bg-white/50 backdrop-blur-sm"
                   placeholder={t('register.confirmPasswordPlaceholder', 'Repite tu contraseña')}
                   required
                 />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
          </div>

          {/* Botón de Registro */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
                             className="w-full bg-gradient-to-r from-[#B3122C] via-[#D50B3F] to-[#B3122C] hover:from-[#D50B3F] hover:to-[#B3122C] text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  {t('employeeRegister.creating', 'Creando cuenta...')}
                </>
              ) : (
                <>
                  <FaUser />
                  {t('employeeRegister.register', 'Registrarse como Empleado')}
                </>
              )}
            </button>
          </div>

          {/* Enlace para iniciar sesión */}
          <div className="text-center pt-4">
            <p className="text-gray-600">
              {t('employeeRegister.alreadyHaveAccount', '¿Ya tienes una cuenta?')}{' '}
              <button
                type="button"
                onClick={() => navigate('/auth/login')}
                                 className="text-red-600 hover:text-red-800 font-medium transition-colors"
              >
                {t('employeeRegister.signIn', 'Inicia sesión')}
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}; 